import { LicenseRequest } from "./models";

interface ObjectOfFunctions {
  [key: string]: Function;
}

interface Tier {
  max: number | boolean;
  value: number;
}

const findAnswerOrFail = (
  licenseRequest: LicenseRequest,
  questionName: string
) => {
  const question = licenseRequest.licenseType.licenseTypeQuestions.find(
    (q) => q.questionName === questionName
  );
  if (!question || question.licenseTypeAnswers.length === 0) {
    throw `No ${questionName} question or answer`;
  }
  return question.licenseTypeAnswers[0].answer;
};

const percentOfAnswer = (
  licenseRequest: LicenseRequest,
  percent: number,
  rrpQuestionName: string,
  unitsQuestionName: string
) => {
  const rrp = findAnswerOrFail(licenseRequest, rrpQuestionName);
  const rrpFloat = parseFloat(rrp);
  const totalSongsInCopyright = findAnswerOrFail(
    licenseRequest,
    "totalSongsInCopyright"
  );
  const totalSongsInCopyrightInt = parseInt(totalSongsInCopyright);
  const units = findAnswerOrFail(licenseRequest, unitsQuestionName);
  const unitsInt = parseInt(units);
  if (isNaN(rrpFloat)) {
    throw `invalid 'Retail Price': it must be a number, but it was ${rrp}`;
  }
  if (isNaN(totalSongsInCopyrightInt)) {
    throw `invalid 'Total Songs In Copyright': it must be an integer, but it was ${totalSongsInCopyright}`;
  }
  if (isNaN(unitsInt)) {
    throw `invalid '${unitsQuestionName}': it must be an integer, but it was ${units}`;
  }
  return (rrpFloat * (percent / 100)) / totalSongsInCopyrightInt;
};

const multiply = (
  licenseRequest: LicenseRequest,
  answers: { [key: string]: number }
) => {
  let retVal = 0;
  const entries = Object.entries(answers);
  for (const e in entries) {
    const answer = findAnswerOrFail(licenseRequest, entries[e][0]);
    const answerFloat = parseFloat(answer);
    if (isNaN(answerFloat)) {
      throw `invalid '${entries[e][0]}: it must be a number, but it was ${answer}`;
    }
    retVal += entries[e][1] * answerFloat;
  }
  return retVal;
};

const perMinute = (licenseRequest: LicenseRequest) => {
  // $0.091 per song, per copy for songs up to 5:00 in length / $0.0175 per minute for songs over 5:00 / Lyric reprint fee would apply
  // Update Jan 2023: 0.12  (for 5 minutes and under), and .0231 cents per minute (for 5:01 and over on song time length)
  // Update Jan 2024: 0.124  (for 5 minutes and under), and .0238 cents per minute (for 5:01 and over on song time length)
  // Update Jan 2025: 0.127 (for 5 minutes and under), and .0245 cents per minute (for 5:01 and over on song time length)
  const playingTimeAnswer = findAnswerOrFail(
    licenseRequest,
    "customerPlayingTime"
  );

  const playingTimeAnswerArr = playingTimeAnswer.split(":");
  if (playingTimeAnswerArr.length !== 2) {
    throw `invalid 'Playing Time' answer: it must look something like 01:23, but it was ${playingTimeAnswer}`;
  }
  if (
    isNaN(parseInt(playingTimeAnswerArr[0])) ||
    isNaN(parseInt(playingTimeAnswerArr[1]))
  ) {
    throw `invalid 'Playing Time' answer: please use numbers to express the playing time instead of ${playingTimeAnswer}`;
  }
  if (
    parseInt(playingTimeAnswerArr[0]) < 5 ||
    (parseInt(playingTimeAnswerArr[0]) === 5 &&
      parseInt(playingTimeAnswerArr[1]) === 0)
  ) {
    return 0.127;
  } else {
    let minutes = parseInt(playingTimeAnswerArr[0]);
    if (parseInt(playingTimeAnswerArr[1]) > 0) {
      minutes++;
    }
    return 0.0245 * minutes;
  }
};

const tiered = (
  licenseRequest: LicenseRequest,
  tieredByQuestion: string,
  tiers: Tier[]
) => {
  /*
  "1 - 50	             £5.00
  51 - 100	£6.00
  101 – 249	£8.00
  250- 499	£10.00
  500 – 1000	£14.00
  1001 – 2000	£16.00
  2001 – 3000	£20.00
  3001 – 5000	£25.00
  5001 – 8000	£30.00
  8001 – 12000	£40.00
  12001  - 20000 £50.00
  20001 – 30000 £65.00
  30001 – 50000 £80.00
  50001 and above £100.00"


  */

  const tieredBy = findAnswerOrFail(licenseRequest, tieredByQuestion);
  const tieredByInt = parseInt(tieredBy);
  if (isNaN(tieredByInt)) {
    throw `invalid 'How many attendees will you have (Estimate)' answer. It should be an integer, but it was ${tieredBy}`;
  }
  for (const t in tiers) {
    if (tiers[t].max === false) {
      return tiers[t].value;
    }
    if (tieredByInt <= (tiers[t].max as number)) {
      return tiers[t].value;
    }
  }
  throw `There was an error with the tiers for this question. This is a problem with the code running this site.`;
};

const percentRrpPrintRunDivNumberOfSongs = (licenseRequest: LicenseRequest) => {
  // 12.5% x RRP x Print run / number of songs in copyright
  const retailPrice = findAnswerOrFail(licenseRequest, "customerRetailPrice");
  // const printRunQuantity = findAnswerOrFail(licenseRequest, "printRunQuantity");
  const totalSongsInCopyright = findAnswerOrFail(
    licenseRequest,
    "totalSongsInCopyright"
  );
  // const printRunQuantityInt = parseInt(printRunQuantity);
  // if (isNaN(printRunQuantityInt)) {
  //   throw `invalid answer for printRunQuantity. It should be an integer, but instead it was this: ${printRunQuantity}`;
  // }
  const totalSongsInCopyrightInt = parseInt(totalSongsInCopyright);
  if (isNaN(totalSongsInCopyrightInt)) {
    throw `invalid answer for totalSongsInCopyright. It should be an integer, but instead it was this: ${totalSongsInCopyright}`;
  }
  const retailPriceFloat = parseFloat(retailPrice);
  if (isNaN(retailPriceFloat)) {
    throw `invalid answer for retailPrice. It should be a number, but instead it was this: ${retailPrice}`;
  }

  return (0.125 * retailPriceFloat) / totalSongsInCopyrightInt;
};

// ((the % rate on the rate card x the retail price) divided by the total # of songs on the project) x the # of uses
const commercialPrintRate = (
  licenseRequest: LicenseRequest,
  numeratorQuestions: string[],
  constant: number,
  denominatorQuestion: string
) => {
  let answer = constant;
  for (const n in numeratorQuestions) {
    const a = findAnswerOrFail(licenseRequest, numeratorQuestions[n]);
    const aFloat = parseFloat(a);
    if (isNaN(aFloat)) {
      throw `invalid answer for ${numeratorQuestions[n]}. It should be an number, but instead it was this: ${a}`;
    }
    answer *= aFloat;
  }
  const denom = findAnswerOrFail(licenseRequest, denominatorQuestion);
  const denomFloat = parseFloat(denom);
  if (isNaN(denomFloat)) {
    throw `invalid answer for ${denominatorQuestion}. It should be an number, but instead it was this: ${denom}`;
  }
  return answer / denomFloat;
};

const multiplyLicenseTypes: { [key: string]: { [key: string]: number } } = {
  "US|Non-Commercial|Master|Rehearsal Track Duplication": {
    numberOfCopies: 0.15,
  },
  "US|Non-Commercial|Master|VBS/Kids Church Audio Master": {
    numberOfCopies: 0.25,
  },
  "US|Non-Commercial|Master|Print Arrangement": {
    numberOfCopies: 0.25,
  },
  "US|Non-Commercial|Master|Print Digital Download": {
    numberOfDownloads: 0.25,
  },
  "US|Non-Commercial|Master|Physical Video Product": {
    totalUnitsForAllFormats: 0.2,
  },
  "US|Non-Commercial|Song|Print Digital Download": { numberOfDownloads: 0.25 },
  "US|Non-Commercial|Song|Print Arrangement": { numberOfCopies: 0.25 },
};

const perMinuteLicenseTypes = [
  "US|Commercial|Song|Karaoke Digital Mechanical",
  "US|Commercial|Song|Karaoke Physical Mechanical",
  "US|Commercial|Song|Split Track Digital Mechanical",
  "US|Commercial|Song|Split Track Physical Mechanical",
  "US|Commercial|Song|Digital Rehearsal Track",
  "US|Commercial|Song|Physical Rehearsal Track",
  "US|Non-Commercial|Song|Rehearsal Track Duplication",
  "US|Non-Commercial|Song|VBS/Kids Church Mechanical",
  "US|Non-Commercial|Song|Digital Download Mechanical",
  "US|Non-Commercial|Song|Physical Mechanical",
  "US|Commercial|Song|Digital Download Mechanical",
  "US|Commercial|Song|Physical Mechanical",
];

const flatRateLicenseTypes: { [key: string]: number } = {
  "US|Non-Commercial|Master|Podcast Use (intro/outro, etc.)": 150,
  "US|Non-Commercial|Song|Podcast Use (intro/outro, etc.)": 150,
  "US|Non-Commercial|Master|Synchronization Digital Download": 0.15,
  "US|Non-Commercial|Song|Synchronization Physical Product": 0.15,
  "US|Commercial|Master|Physical Video Product": 0.15,
  "US|Commercial|Song|Physical Synchronization": 0.15,
  "US|Non-Commercial|Song|Synchronization Digital Download": 0.15,
  // it should multiple 300 by the per song rate.   So $0.15 X 300 = $45.00 .  Then the advance fee is populated on the contract template in that slot.    The reason we need OWLs to calculate that is if we give the customer a lower rate than $0.15…so maybe we give them $0.12 or $0.10….the system can still calculate the advance fee for 300 downloads.
  "US|Non-Commercial|Master|Digital Video Download": 0.15,
  "US|Non-Commercial|Song|Choral Photocopy/Duplication": 0.75,
  "US|Non-Commercial|Song|Print Photocopy/Duplication (Non-Choral)": 0.25,
  // $35.00 per song, per year
  // This is just always for one year. Then it expires. Then they come back and buy another one for the next year.
  "US|Non-Commercial|Song|Lyric Display": 35,
  "US|Non-Commercial|Song|Print Bulletin/Program": 35,
  // minimum $30.00 per song, per year
  "US|Non-Commercial|Master|Digital Video Streaming": 30,
  // £15 per 20 copies (min charge £15)
  "UK|Non-Commercial|Song|Print Photocopy/Duplication (Kingsway Publication)": 15,
  // £7.50 per 20 copies (min charge £15)
  "UK|Non-Commercial|Song|Print Photocopy/Duplication": 7.5,
  "UK|Non-Commercial|Song|Print Arrangement": 7.5,
  "UK|Non-Commercial|Master|Audio Stream": 15,
  "UK|Non-Commercial|Master|Digital Video Streaming": 15,
  "UK|Non-Commercial|Master|Podcast Use (intro/outro, etc.)": 15,
  "UK|Non-Commercial|Master|Website Use": 15,
  "UK|Non-Commercial|Song|Website Use": 15,
  "UK|Non-Commercial|Master|TV Use": 15,
  "UK|Commercial|Master|Website Use": 15,
  "US|Non-Commercial|Song|Lyric Reprint": 50,
};

const negotiatedLicenseTypes = [
  "US|Commercial|Song|Digital Lyric Reprint",
  "US|Commercial|Song|Digital Print Lyric Reprint (eBook)",
  "US|Commercial|Master|Digital Video Download",
  "US|Commercial|Song|Digital Video Streaming",
  "US|Commercial|Song|Synchronization Streaming Lyric Video",
  "US|Commercial|Master|Digital Video Streaming",
  "US|Commercial|Song|Synchronization License (Digital Lyric Video Download)",
  "US|Commercial|Song|Performance",
  "US|Commercial|Master|Physical Audio Product",
  "US|Commercial|Song|Physical Print Lyric Reprint (Book)",
  "US|Commercial|Song|Print Lyric Reprint",
  "US|Commercial|Song|Website Use",
  "US|Commercial|Master|Website Use",
  "US|Commercial|Song|Other/Misc",
  "US|Commercial|Master|Other/Misc",
  "US|Commercial|Master|Digital Audio Product",
  "US|Non-Commercial|Master|Digital Audio Product",
  "US|Non-Commercial|Song|Performance",
  "UK|Non-Commercial|Song|Performance",
  "US|Non-Commercial|Song|Radio Use (bumpers, into/outro, etc)",
  "US|Non-Commercial|Song|TV Synchronization",
  "US|Non-Commercial|Master|Physical Audio Product",
  "US|Non-Commercial|Master|Radio Use (bumpers, into/outro, etc)",
  "US|Non-Commercial|Master|TV Use",
  // The internal user will know the $30 threshold. Treat this as "Negotiated"
  "US|Non-Commercial|Song|Website Use",
  "US|Non-Commercial|Master|Website Use",
  "UK|Non-Commercial|Master|Rehearsal Track Duplication",
  "UK|Non-Commercial|Master|Performance",
  "UK|Non-Commercial|Song|Print Digital Download",
  "UK|Non-Commercial|Song|TV Synchronization",
  "UK|Non-Commercial|Master|Digital Audio Product",
  "UK|Non-Commercial|Master|Digital Video Download",
  "UK|Non-Commercial|Master|Physical Audio Product",
  "UK|Non-Commercial|Master|Physical Video Product",
  "UK|Non-Commercial|Song|Synchronization Digital Download",
  "US|Commercial|Song|Synchronization Digital Download",
  "UK|Non-Commercial|Song|Synchronization Physical Product",
  "UK|Commercial|Song|Digital Lyric Reprint",
  "UK|Commercial|Song|Print Digital Download",
  "UK|Commercial|Song|Digital Video Streaming",
  "UK|Commercial|Song|Performance",
  "UK|Commercial|Song|Physical Synchronization",
  "UK|Commercial|Master|Digital Video Streaming",
  "UK|Commercial|Master|Physical Audio Product",
  "UK|Commercial|Master|Digital Audio Product",
  "UK|Commercial|Master|Digital Video Download",
  "UK|Commercial|Master|Physical Video Product",
  "UK|Commercial|Master|Synchronization Digital Download",
  "UK|Commercial|Song|Synchronization Digital Download",
  "UK|Commercial|Song|Website Use",
  "UK|Commercial|Song|Print Lyric Reprint",
  "US|Non-Commercial|Master|Synchronization Physical Product",
  "US|Non-Commercial|Song|Other/Misc",
  "US|Commercial|Song|Other/Misc",
  "UK|Non-Commercial|Song|Other/Misc",
  "UK|Commercial|Song|Other/Misc",
  "US|Non-Commercial|Master|Other/Misc",
  "US|Commercial|Master|Other/Misc",
  "UK|Non-Commercial|Master|Other/Misc",
  "UK|Commercial|Master|Other/Misc",
  "UK|Non-Commercial|Song|Lyric Reprint - Non-Music",
  // These next three license types don't have a 'rate' to be calculated. I'm putting them in this array so that the
  // 'this license type has not been implemented' banner doesn't show
  "US|Commercial|Song|Audio Digital STEM Files",
  "US|Commercial|Master|Audio Digital STEM Files",
  "US|Commercial|Song|Audio Physical STEM Files",
  "UK|Commercial|Song|Digital Print - Piano/Vocal/Guitar",
  "UK|Commercial|Song|Digital Print - Anthem/Octavo",
];

const percentageOfAnswerLicenseTypes: {
  [key: string]: {
    percent: number;
    rrpQuestionName: string;
    unitsQuestionName: string;
  };
} = {
  "UK|Commercial|Song|Digital Print - Chord Chart/Rhythm Chart": {
    percent: 25,
    rrpQuestionName: "customerRetailPrice",
    unitsQuestionName: "estimatedNumberOfDownloads",
  },
  "UK|Commercial|Song|Digital Print - Lead Sheet": {
    percent: 25,
    rrpQuestionName: "customerRetailPrice",
    unitsQuestionName: "estimatedNumberOfDownloads",
  },
  "UK|Commercial|Song|Digital Print - Orchestration/Score": {
    percent: 25,
    rrpQuestionName: "customerRetailPrice",
    unitsQuestionName: "estimatedNumberOfDownloads",
  },
};

const tieredLicenseTypes: {
  [key: string]: { tieredBy: string; tiers: Tier[] };
} = {
  "UK|Non-Commercial|Song|Event License": {
    tieredBy: "attendeesEstimate",
    tiers: [
      { max: 50, value: 5 },
      { max: 100, value: 6 },
      { max: 249, value: 8 },
      { max: 499, value: 10 },
      { max: 1000, value: 14 },
      { max: 2000, value: 16 },
      { max: 3000, value: 20 },
      { max: 5000, value: 25 },
      { max: 8000, value: 30 },
      { max: 12000, value: 40 },
      { max: 20000, value: 50 },
      { max: 30000, value: 65 },
      { max: 50000, value: 80 },
      { max: false, value: 100 },
    ],
  },
  "UK|Non-Commercial|Song|Lyric Display": {
    tieredBy: "attendeesEstimate",
    tiers: [
      { max: 50, value: 5 },
      { max: 100, value: 6 },
      { max: 249, value: 8 },
      { max: 499, value: 10 },
      { max: 1000, value: 14 },
      { max: 2000, value: 16 },
      { max: 3000, value: 20 },
      { max: 5000, value: 25 },
      { max: 8000, value: 30 },
      { max: 12000, value: 40 },
      { max: 20000, value: 50 },
      { max: 30000, value: 65 },
      { max: 50000, value: 80 },
      { max: false, value: 100 },
    ],
  },
  "UK|Non-Commercial|Song|Print Bulletin/Programme": {
    tieredBy: "attendeesEstimate",
    tiers: [
      { max: 50, value: 5 },
      { max: 100, value: 6 },
      { max: 249, value: 8 },
      { max: 499, value: 10 },
      { max: 1000, value: 14 },
      { max: 2000, value: 16 },
      { max: 3000, value: 20 },
      { max: 5000, value: 25 },
      { max: 8000, value: 30 },
      { max: 12000, value: 40 },
      { max: 20000, value: 50 },
      { max: 30000, value: 60 },
      { max: 50000, value: 70 },
      { max: false, value: 100 },
    ],
  },
  "UK|Non-Commercial|Song|Physical Songbook Print (Words & Music)": {
    tieredBy: "printRunQuantity",
    tiers: [
      { max: 50, value: 5 },
      { max: 100, value: 6 },
      { max: 249, value: 8 },
      { max: 499, value: 10 },
      { max: 1000, value: 14 },
      { max: 2000, value: 16 },
      { max: 3000, value: 20 },
      { max: 5000, value: 25 },
      { max: 8000, value: 30 },
      { max: 12000, value: 40 },
      { max: 20000, value: 50 },
      { max: 30000, value: 70 },
      { max: 50000, value: 85 },
      { max: false, value: 100 },
    ],
  },
};

const percentRrpPrintRunDivNumberOfSongsLicenseTypes = [
  "UK|Commercial|Song|Physical Songbook Print (Words & Music)",
];

const licenseTypeFunctions: ObjectOfFunctions = {
  "US|Non-Commercial|Song|Conference Use": (licenseRequest: LicenseRequest) => {
    // "$35.00 per song for up to 5 days / $5.00 per song for each day thereafter (limited to 7 days)"
    const beginningDate = findAnswerOrFail(
      licenseRequest,
      "conferenceBeginningDate"
    );
    const endDate = findAnswerOrFail(licenseRequest, "conferenceEndDate");
    const days =
      Math.ceil(
        (new Date(endDate).getTime() - new Date(beginningDate).getTime()) /
          (3600000 * 24)
      ) + 1;
    if (isNaN(days)) {
      throw "invalid dates: unable to calculate event length";
    } else if (days <= 0) {
      throw "invalid dates: the beginning date comes after the end date";
    } else if (days <= 5) {
      return 35;
    } else {
      const weeks = Math.floor(days / 5);
      const leftOverDays = days % 5;
      return weeks * 35 + leftOverDays * 5;
    }
  },
  "US|Non-Commercial|Song|Digital Video Streaming": (
    LicenseRequest: LicenseRequest
  ) => {
    // minimum $30.00 per song, per year
    const months = findAnswerOrFail(LicenseRequest, "numberOfMonthsRequested");
    const monthsInt = parseInt(months);
    if (isNaN(monthsInt)) {
      throw `invalid answer for numberOfMonthsRequested. It should be an integer, but instead it was this: ${months}`;
    }
    return Math.ceil(monthsInt / 12) * 30;
  },
  "US|Non-Commercial|Song|Print Physical Songbook": (
    licenseRequest: LicenseRequest
  ) => {
    // $0.50 per song, per copy up to 100 copies / $0.25 per song, per copy over 100 copies
    let total = 0;
    const copies = findAnswerOrFail(licenseRequest, "numberOfCopies");
    const copiesInt = parseInt(copies);
    if (isNaN(copiesInt)) {
      throw `invalid answer for copies. It should be an integer, but instead it was this: ${copies}`;
    }
    if (copiesInt <= 100) {
      return copiesInt * 0.5;
    } else {
      return 50 + (copiesInt - 100) * 0.25;
    }
  },
  "UK|Commercial|Song|Digital Lyric Reprint (eBook) - Non-Music; i.e. biography":
    (licenseRequest: LicenseRequest) => {
      // £75 + VAT - up to 3000 downloads
      const downloads = findAnswerOrFail(licenseRequest, "projectedDownloads");
      const downloadsInt = parseInt(downloads);
      if (isNaN(downloadsInt)) {
        throw `invalid answer for projectedDownloads. It should be an integer, but instead it was this: ${downloads}`;
      }
      return Math.ceil(downloadsInt / 3000) * 75;
    },
  "UK|Commercial|Song|Digital Lyric Reprint (eBook) - Songbook": (
    licenseRequest: LicenseRequest
  ) => {
    const retailPrice = findAnswerOrFail(licenseRequest, "customerRetailPrice");
    const totalSongs = findAnswerOrFail(
      licenseRequest,
      "totalSongsInCopyright"
    );
    const totalSongsInt = parseInt(totalSongs);
    if (isNaN(totalSongsInt)) {
      throw `invalid answer for totalSongsInCopyright. It should be an integer, but instead it was this: ${totalSongs}`;
    }
    const retailPriceFloat = parseFloat(retailPrice);
    if (isNaN(retailPriceFloat)) {
      throw `invalid answer for retailPrice. It should be a number, but instead it was this: ${retailPrice}`;
    }

    return (0.25 * retailPriceFloat) / totalSongsInt;
  },

  "UK|Commercial|Song|Digital Print Sheet Music": (
    licenseRequest: LicenseRequest
  ) => {
    const retailPrice = findAnswerOrFail(licenseRequest, "customerRetailPrice");
    const totalSongs = findAnswerOrFail(
      licenseRequest,
      "totalSongsInCopyright"
    );
    const totalSongsInt = parseInt(totalSongs);
    if (isNaN(totalSongsInt)) {
      throw `invalid answer for totalSongsInCopyright. It should be an integer, but instead it was this: ${totalSongs}`;
    }
    const retailPriceFloat = parseFloat(retailPrice);
    if (isNaN(retailPriceFloat)) {
      throw `invalid answer for retailPrice. It should be a number, but instead it was this: ${retailPrice}`;
    }

    return (0.25 * retailPriceFloat) / totalSongsInt;
  },
  "UK|Commercial|Song|Physical Print - Anthem/Octavo": (
    licenseRequest: LicenseRequest
  ) => {
    const retailPrice = findAnswerOrFail(licenseRequest, "customerRetailPrice");
    const totalSongs = findAnswerOrFail(
      licenseRequest,
      "totalSongsInCopyright"
    );
    const totalSongsInt = parseInt(totalSongs);
    if (isNaN(totalSongsInt)) {
      throw `invalid answer for totalSongsInCopyright. It should be an integer, but instead it was this: ${totalSongs}`;
    }
    const retailPriceFloat = parseFloat(retailPrice);
    if (isNaN(retailPriceFloat)) {
      throw `invalid answer for retailPrice. It should be a number, but instead it was this: ${retailPrice}`;
    }

    return (0.125 * retailPriceFloat) / totalSongsInt;
  },
  "UK|Commercial|Song|Physical Lyric Print (Words Edition) - Songbook": (
    licenseRequest: LicenseRequest
  ) => {
    const retailPrice = findAnswerOrFail(licenseRequest, "customerRetailPrice");
    const totalSongs = findAnswerOrFail(
      licenseRequest,
      "totalSongsInCopyright"
    );
    const totalSongsInt = parseInt(totalSongs);
    if (isNaN(totalSongsInt)) {
      throw `invalid answer for totalSongsInCopyright. It should be an integer, but instead it was this: ${totalSongs}`;
    }
    const retailPriceFloat = parseFloat(retailPrice);
    if (isNaN(retailPriceFloat)) {
      throw `invalid answer for retailPrice. It should be a number, but instead it was this: ${retailPrice}`;
    }

    return (0.125 * retailPriceFloat) / totalSongsInt;
  },

  "UK|Commercial|Song|Digital Songbook (ebook)": (
    licenseRequest: LicenseRequest
  ) => {
    // 25% x RRP  / number of songs in copyright
    const retailPrice = findAnswerOrFail(licenseRequest, "customerRetailPrice");
    const totalSongs = findAnswerOrFail(
      licenseRequest,
      "totalSongsInCopyright"
    );
    const totalSongsInt = parseInt(totalSongs);
    if (isNaN(totalSongsInt)) {
      throw `invalid answer for totalSongsInCopyright. It should be an integer, but instead it was this: ${totalSongs}`;
    }
    const retailPriceFloat = parseFloat(retailPrice);
    if (isNaN(retailPriceFloat)) {
      throw `invalid answer for retailPrice. It should be a number, but instead it was this: ${retailPrice}`;
    }

    return (0.25 * retailPriceFloat) / totalSongsInt;
  },
  "UK|Commercial|Song|Physical Print Lyric Reprint (Book)": (
    licenseRequest: LicenseRequest
  ) => {
    // £75 + VAT per song - up to 10,000 print run
    const copies = findAnswerOrFail(licenseRequest, "numberOfCopies");
    const copiesInt = parseInt(copies);
    if (isNaN(copiesInt)) {
      throw `invalid answer for copies. It should be an integer, but instead it was this: ${copies}`;
    }
    return Math.ceil(copiesInt / 10000) * 75;
  },
  "UK|Commercial|Song|Physical Lyric Print (Words Edition) - Non-Music": (
    licenseRequest: LicenseRequest
  ) => {
    // (12.5% x retail price) divided by the # of songs in copyright
    const retailPrice = findAnswerOrFail(licenseRequest, "customerRetailPrice");
    const totalSongs = findAnswerOrFail(
      licenseRequest,
      "totalSongsInCopyright"
    );
    const totalSongsInt = parseInt(totalSongs);
    if (isNaN(totalSongsInt)) {
      throw `invalid answer for totalSongsInCopyright. It should be an integer, but instead it was this: ${totalSongs}`;
    }
    const retailPriceFloat = parseFloat(retailPrice);
    if (isNaN(retailPriceFloat)) {
      throw `invalid answer for retailPrice. It should be a number, but instead it was this: ${retailPrice}`;
    }

    return (0.125 * retailPriceFloat) / totalSongsInt;
  },
};

const commercialPrintRateLicenseTypes: {
  [key: string]: {
    numeratorQuestions: string[];
    constant: number;
    denominatorQuestion: string;
  };
} = {
  "US|Commercial|Song|Digital Print Sheet Music": {
    numeratorQuestions: ["customerRetailPrice", "timesSongAppearsOnProject"],
    constant: 0.25,
    denominatorQuestion: "songsInCustomerProduct",
  },
  "US|Commercial|Song|Print Physical Songbook": {
    numeratorQuestions: ["customerRetailPrice", "timesSongAppearsOnProject"],
    constant: 0.125,
    denominatorQuestion: "songsInCustomerProduct",
  },
  "US|Commercial|Song|Print Digital Download": {
    numeratorQuestions: ["customerRetailPrice", "timesSongAppearsOnProject"],
    constant: 0.25,
    denominatorQuestion: "songsInCustomerProduct",
  },
  "US|Commercial|Song|Digital Print - Anthem/Octavo": {
    numeratorQuestions: ["customerRetailPrice", "timesSongAppearsOnProject"],
    constant: 0.25,
    denominatorQuestion: "songsInCustomerProduct",
  },
  "US|Commercial|Song|Digital Print - Chord Chart/Rhythm Chart": {
    numeratorQuestions: ["customerRetailPrice", "timesSongAppearsOnProject"],
    constant: 0.25,
    denominatorQuestion: "songsInCustomerProduct",
  },
  "US|Commercial|Song|Digital Print - Lead Sheet": {
    numeratorQuestions: ["customerRetailPrice", "timesSongAppearsOnProject"],
    constant: 0.25,
    denominatorQuestion: "songsInCustomerProduct",
  },
  "US|Commercial|Song|Digital Print - Orchestration/Score": {
    numeratorQuestions: ["customerRetailPrice", "timesSongAppearsOnProject"],
    constant: 0.25,
    denominatorQuestion: "songsInCustomerProduct",
  },
  "US|Commercial|Song|Digital Print - Piano/Vocal/Guitar": {
    numeratorQuestions: ["customerRetailPrice", "timesSongAppearsOnProject"],
    constant: 0.25,
    denominatorQuestion: "songsInCustomerProduct",
  },
  "US|Commercial|Song|Physical Print - Anthem/Octavo": {
    numeratorQuestions: ["customerRetailPrice", "timesSongAppearsOnProject"],
    constant: 0.125,
    denominatorQuestion: "songsInCustomerProduct",
  },
  "US|Commercial|Song|Physical Print Lead Sheet": {
    numeratorQuestions: ["customerRetailPrice", "timesSongAppearsOnProject"],
    constant: 0.125,
    denominatorQuestion: "songsInCustomerProduct",
  },
};

const rateCalculation = (licenseRequest: LicenseRequest) => {
  if (!licenseRequest.work) {
    throw "the licenseRequest must include the song/master";
  }
  const { licenseType } = licenseRequest;
  const licenseTypeLabel: string =
    licenseType.location +
    "|" +
    (licenseType.commercial ? "Commercial" : "Non-Commercial") +
    "|" +
    licenseType.songMaster +
    "|" +
    licenseType.name;
  if (negotiatedLicenseTypes.some((lt) => lt === licenseTypeLabel)) {
    return "";
  }
  if (flatRateLicenseTypes[licenseTypeLabel]) {
    return flatRateLicenseTypes[licenseTypeLabel];
  }
  if (percentageOfAnswerLicenseTypes[licenseTypeLabel]) {
    return percentOfAnswer(
      licenseRequest,
      percentageOfAnswerLicenseTypes[licenseTypeLabel].percent,
      percentageOfAnswerLicenseTypes[licenseTypeLabel].rrpQuestionName,
      percentageOfAnswerLicenseTypes[licenseTypeLabel].unitsQuestionName
    );
  }
  if (licenseTypeFunctions[licenseTypeLabel]) {
    return licenseTypeFunctions[licenseTypeLabel](licenseRequest);
  }
  if (perMinuteLicenseTypes.some((lt) => lt === licenseTypeLabel)) {
    return perMinute(licenseRequest);
  }
  if (multiplyLicenseTypes[licenseTypeLabel]) {
    return multiply(licenseRequest, multiplyLicenseTypes[licenseTypeLabel]);
  }
  if (tieredLicenseTypes[licenseTypeLabel]) {
    return tiered(
      licenseRequest,
      tieredLicenseTypes[licenseTypeLabel].tieredBy,
      tieredLicenseTypes[licenseTypeLabel].tiers
    );
  }
  if (
    percentRrpPrintRunDivNumberOfSongsLicenseTypes.some(
      (lt) => lt === licenseTypeLabel
    )
  ) {
    return percentRrpPrintRunDivNumberOfSongs(licenseRequest);
  }
  if (commercialPrintRateLicenseTypes[licenseTypeLabel]) {
    return commercialPrintRate(
      licenseRequest,
      commercialPrintRateLicenseTypes[licenseTypeLabel].numeratorQuestions,
      commercialPrintRateLicenseTypes[licenseTypeLabel].constant,
      commercialPrintRateLicenseTypes[licenseTypeLabel].denominatorQuestion
    );
  }
  throw `this licenseType has not yet been implemented: ${licenseTypeLabel}`;
};

export { rateCalculation };
